import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Trash2, RotateCcw, Plus, X, Upload, Paperclip, Award, Lock,
  User, FileText, ClipboardCheck, Clock, Shield, Briefcase, ChevronRight, Compass,
} from 'lucide-react';
import api, { empleadosAPI, obligacionesAPI, gestionDocumentalAPI, textoErrorApi } from '../api/api';
import { urlConToken } from '../utils/urlConToken';
import VisorDocumento from '../components/VisorDocumento';
import RegistroHorarioEmpleado, { useAvisoTemporal, AvisoInline, DialogoConfirmar } from '../components/empleados/RegistroHorarioEmpleado';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { hoyMadrid } from '../utils/fechas';
import { costePersonalEvento, esCuentaTecnica } from '../utils/empleadosEventos';
import { MODULOS_ASIGNABLES, MODULOS_POR_GRUPO } from '../utils/modulosErp';

// Ficha única del empleado: alta (/empleados/nuevo, en dos pasos) y
// consulta/edición (/empleados/:id, por pestañas). Sin autoguardado: botón
// Guardar explícito y aviso de cambios sin guardar al salir.

// ==================== FORMATOS ====================

// "2026-10-06" (o ISO completo) -> "06/10/2026", sin pasar por zonas horarias
const fechaLarga = (valor) => {
  if (!valor) return '';
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(valor);
};

// "2026-10-06" -> "06/10"
const fechaCorta = (valor) => {
  if (!valor) return '';
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : String(valor);
};

// Fecha y hora en Madrid de un instante guardado en UTC (con o sin zona)
const fechaHoraMadrid = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const conZona = /[zZ]|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`;
  const d = new Date(conZona);
  if (Number.isNaN(d.getTime())) return texto;
  return d.toLocaleString('es-ES', {
    timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// Antigüedad en años y meses hasta hoy (Madrid)
const antiguedadTexto = (desde) => {
  const m = String(desde || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const h = hoyMadrid().split('-').map(Number);
  let meses = (h[0] - Number(m[1])) * 12 + (h[1] - Number(m[2]));
  if (h[2] < Number(m[3])) meses -= 1;
  if (meses < 0) return 'Aún no ha empezado';
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const partes = [];
  if (anos) partes.push(`${anos} ${anos === 1 ? 'año' : 'años'}`);
  if (resto) partes.push(`${resto} ${resto === 1 ? 'mes' : 'meses'}`);
  return partes.length ? partes.join(' y ') : 'Menos de un mes';
};

// Marca textual de cada punto de la lista de obligaciones
const marcaObligacion = (item) => {
  if (item.ok === true) return { texto: 'Hecho', clase: 'bg-green-100 text-green-800' };
  if (item.ok === null || item.ok === undefined) return { texto: 'No aplica aún', clase: 'bg-gray-100 text-gray-600' };
  if (String(item.detalle || '').startsWith('Caducado')) return { texto: 'Caducado', clase: 'bg-red-100 text-red-700' };
  return { texto: 'Pendiente', clase: 'bg-red-100 text-red-700' };
};

// Nombre legible del tipo documental (plantillas de Gestión Documental)
const NOMBRES_TIPO_DOCUMENTAL = {
  EPIS: 'EPIS',
  Fichas_Informativas: 'Fichas informativas de riesgos',
  PRL: 'PRL',
  Reconocimiento_Medico: 'Reconocimiento médico',
  Politicas: 'Políticas',
  Contrato: 'Contrato',
  Otros: 'Otros',
};
const nombreTipoDocumental = (tipo) => NOMBRES_TIPO_DOCUMENTAL[tipo] || String(tipo || '').replace(/_/g, ' ');

// Asignación todavía en curso (el empleado la tiene pendiente o falta validarla)
const ESTADOS_ASIGNACION_VIVA = ['Pendiente', 'Descargado', 'Firmado'];

// Texto del estado de la asignación de un punto con documento
const estadoAsignacionTexto = (asig) => {
  if (!asig) return '';
  const limite = asig.fecha_limite ? `, fecha límite ${fechaCorta(asig.fecha_limite)}` : '';
  if (asig.notas_rechazo && (asig.estado === 'Pendiente' || asig.estado === 'Rechazado')) {
    return `Rechazado: ${asig.notas_rechazo}`;
  }
  switch (asig.estado) {
    case 'Pendiente': {
      const enviado = asig.fecha_asignacion || asig.created_at;
      return `${enviado ? `Enviado el ${fechaCorta(enviado)}` : 'Enviado al empleado'}${limite}`;
    }
    case 'Descargado':
      return `Descargado${limite}`;
    case 'Firmado':
      return `Firmado${asig.fecha_firma ? ` el ${fechaCorta(asig.fecha_firma)}` : ''}, pendiente de validar`;
    case 'Validado':
      return `Validado${asig.fecha_validacion ? ` el ${fechaCorta(asig.fecha_validacion)}` : ''}`;
    case 'Rechazado':
      return 'Rechazado';
    default:
      return asig.estado || '';
  }
};

// ==================== CATÁLOGOS ====================

const COLORES = [
  { value: 'amarillo', label: 'Amarillo', bg: 'bg-yellow-400', text: 'text-yellow-800' },
  { value: 'azul', label: 'Azul', bg: 'bg-blue-500', text: 'text-white' },
  { value: 'verde', label: 'Verde', bg: 'bg-green-500', text: 'text-white' },
  { value: 'naranja', label: 'Naranja', bg: 'bg-orange-500', text: 'text-white' },
  { value: 'morado', label: 'Morado', bg: 'bg-purple-500', text: 'text-white' },
  { value: 'rosa', label: 'Rosa', bg: 'bg-pink-400', text: 'text-white' },
  { value: 'rojo', label: 'Rojo', bg: 'bg-red-500', text: 'text-white' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500', text: 'text-white' },
];

const clasesColor = (color) => {
  const c = COLORES.find((col) => col.value === color);
  return c ? `${c.bg} ${c.text}` : 'bg-gray-200 text-gray-700';
};

const TIPOS_CONTRATO = [
  { value: 'indefinido', label: 'Indefinido' },
  { value: 'temporal', label: 'Temporal' },
  { value: 'fijo_discontinuo', label: 'Fijo discontinuo' },
  { value: 'practicas', label: 'Prácticas' },
  { value: 'otro', label: 'Otro' },
];

const JORNADAS = [
  { value: 'completa', label: 'Completa' },
  { value: 'parcial', label: 'Parcial' },
];

const TIPOS_DOCUMENTO = [
  { value: 'nomina', label: 'Nómina' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'certificado', label: 'Certificado' },
  { value: 'formacion', label: 'Formación' },
  { value: 'dni', label: 'DNI' },
  { value: 'seguridad_social', label: 'Seguridad Social' },
  { value: 'otro', label: 'Otro' },
];

const PESTANAS = [
  { clave: 'datos', texto: 'Datos', icono: User },
  { clave: 'contrato', texto: 'Contrato y nómina', icono: Briefcase },
  { clave: 'obligaciones', texto: 'Obligaciones', icono: ClipboardCheck },
  { clave: 'documentos', texto: 'Documentos y formación', icono: FileText },
  { clave: 'fichajes', texto: 'Fichajes', icono: Clock },
  { clave: 'acceso', texto: 'Acceso y permisos', icono: Shield },
];
const CLAVES_PESTANAS = PESTANAS.map((p) => p.clave);

// ==================== FORMULARIO ====================

// Pantalla al entrar (pestaña Acceso): auto se guarda sin valor (null)
const PANTALLAS_FIJAS = ['calendario', 'mi_inicio'];
const OPCIONES_PANTALLA_INICIO = [
  { valor: 'auto', texto: 'Automática: Mi Inicio hasta que ficha, después el calendario' },
  { valor: 'calendario', texto: 'Siempre el calendario' },
  { valor: 'mi_inicio', texto: 'Siempre Mi Inicio' },
];

const fecha10 = (v) => (v ? String(v).slice(0, 10) : '');
const textoONada = (v) => (v === null || v === undefined ? '' : String(v));

// Formulario (todo en texto o booleano) a partir de la ficha guardada
const formDesdeEmpleado = (emp) => {
  const ce = emp?.contacto_emergencia && typeof emp.contacto_emergencia === 'object' ? emp.contacto_emergencia : {};
  return {
    nombre: emp?.nombre || '',
    nombre_completo: emp?.nombre_completo || '',
    dni: emp?.dni || '',
    fecha_nacimiento: fecha10(emp?.fecha_nacimiento),
    nacionalidad: emp?.nacionalidad || '',
    caducidad_nie: fecha10(emp?.caducidad_nie),
    telefono: emp?.telefono || '',
    email: emp?.email || '',
    direccion: emp?.direccion || '',
    ce_nombre: ce.nombre || '',
    ce_telefono: ce.telefono || '',
    ce_relacion: ce.relacion || '',
    color: emp?.color || '',
    coste_personal_evento: emp?.coste_personal_evento ?? null,
    es_cuenta_tecnica: typeof emp?.es_cuenta_tecnica === 'boolean' ? emp.es_cuenta_tecnica : null,
    no_va_a_eventos: emp?.no_va_a_eventos === true,
    carnet_conducir: !!emp?.carnet_conducir,
    talla_uniforme: emp?.talla_uniforme || '',
    rol: emp?.rol || 'operaciones',
    tipo_contrato: emp?.tipo_contrato || '',
    fecha_alta: fecha10(emp?.fecha_alta),
    fecha_antiguedad: fecha10(emp?.fecha_antiguedad),
    fecha_fin_contrato: fecha10(emp?.fecha_fin_contrato),
    jornada: emp?.jornada || '',
    horas_contrato_semana: textoONada(emp?.horas_contrato_semana ?? 40),
    categoria_convenio: emp?.categoria_convenio || '',
    dias_vacaciones_anuales: textoONada(emp?.dias_vacaciones_anuales ?? 30),
    salario_bruto: textoONada(emp?.salario_bruto ?? emp?.salario ?? 0),
    retencion_irpf: textoONada(emp?.retencion_irpf),
    numero_ss: emp?.numero_ss || '',
    iban: emp?.iban || '',
    exento_ss_empresa: !!emp?.exento_ss_empresa,
    sin_nomina: !!emp?.sin_nomina,
    pantalla_inicio: PANTALLAS_FIJAS.includes(emp?.pantalla_inicio) ? emp.pantalla_inicio : 'auto',
  };
};

const formAlta = () => ({
  ...formDesdeEmpleado(null),
  fecha_alta: hoyMadrid(),
  retencion_irpf: '15',
});

const textoONull = (v) => {
  const t = String(v ?? '').trim();
  return t ? t : null;
};
const numeroO = (v, porDefecto) => {
  if (v === '' || v === null || v === undefined) return porDefecto;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : porDefecto;
};

// Cuerpo para el API. Vacíos como null; `salario` = `salario_bruto` y
// `cuenta_bancaria` = IBAN (el servidor también lo hace). `titular_cuenta`,
// `foto` y `coste_despido` ya no se piden.
const cuerpoDesdeForm = (f) => {
  const ce = {
    nombre: textoONull(f.ce_nombre),
    telefono: textoONull(f.ce_telefono),
    relacion: textoONull(f.ce_relacion),
  };
  const salarioBruto = numeroO(f.salario_bruto, 0);
  const iban = textoONull(f.iban);
  return {
    nombre: String(f.nombre || '').trim(),
    nombre_completo: textoONull(f.nombre_completo),
    dni: textoONull(f.dni),
    fecha_nacimiento: f.fecha_nacimiento || null,
    nacionalidad: textoONull(f.nacionalidad),
    caducidad_nie: f.caducidad_nie || null,
    telefono: textoONull(f.telefono),
    email: textoONull(f.email),
    direccion: textoONull(f.direccion),
    contacto_emergencia: ce.nombre || ce.telefono || ce.relacion ? ce : null,
    color: f.color || null,
    coste_personal_evento: f.coste_personal_evento || null,
    es_cuenta_tecnica: typeof f.es_cuenta_tecnica === 'boolean' ? f.es_cuenta_tecnica : null,
    no_va_a_eventos: !!f.no_va_a_eventos,
    carnet_conducir: !!f.carnet_conducir,
    talla_uniforme: textoONull(f.talla_uniforme),
    rol: f.rol || 'operaciones',
    tipo_contrato: f.tipo_contrato || null,
    fecha_alta: f.fecha_alta || hoyMadrid(),
    fecha_antiguedad: f.fecha_antiguedad || null,
    fecha_fin_contrato: f.fecha_fin_contrato || null,
    jornada: f.jornada || null,
    horas_contrato_semana: numeroO(f.horas_contrato_semana, 40),
    categoria_convenio: textoONull(f.categoria_convenio),
    dias_vacaciones_anuales: Math.round(numeroO(f.dias_vacaciones_anuales, 30)),
    salario_bruto: salarioBruto,
    salario: salarioBruto,
    retencion_irpf: f.retencion_irpf === '' ? null : numeroO(f.retencion_irpf, null),
    numero_ss: textoONull(f.numero_ss),
    iban,
    cuenta_bancaria: iban,
    exento_ss_empresa: !!f.exento_ss_empresa,
    sin_nomina: !!f.sin_nomina,
    pantalla_inicio: PANTALLAS_FIJAS.includes(f.pantalla_inicio) ? f.pantalla_inicio : null,
  };
};

// Solo lo que ha cambiado respecto a lo guardado (el PUT aplica solo lo enviado)
const cuerpoCambios = (form, original) => {
  const nuevo = cuerpoDesdeForm(form);
  const antes = cuerpoDesdeForm(original);
  const cambios = {};
  Object.keys(nuevo).forEach((k) => {
    if (JSON.stringify(nuevo[k]) !== JSON.stringify(antes[k])) cambios[k] = nuevo[k];
  });
  return cambios;
};

// Errores que se ven antes de llamar al servidor
const erroresBasicos = (f) => {
  const errores = [];
  if (!String(f.nombre || '').trim()) errores.push('Falta el nombre corto');
  if (!f.fecha_alta) errores.push('Falta la fecha de alta');
  if (f.fecha_fin_contrato && f.fecha_alta && f.fecha_fin_contrato < f.fecha_alta) {
    errores.push('El fin de contrato no puede ser anterior a la fecha de alta');
  }
  return errores;
};

// Aviso del navegador al cerrar o recargar la pestaña con cambios sin guardar
const useAvisoAlSalir = (hayCambios) => {
  useEffect(() => {
    if (!hayCambios) return undefined;
    const alSalir = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);
};

// ==================== PIEZAS DEL FORMULARIO ====================

const CLASE_INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm';

const Campo = ({ etiqueta, ayuda, className = '', children }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-medium text-gray-700 mb-1">{etiqueta}</span>
    {children}
    {ayuda && <span className="block text-xs text-gray-500 mt-1">{ayuda}</span>}
  </label>
);

const Seccion = ({ titulo, children, testId }) => (
  <section className="bg-white rounded-lg border border-gray-200 p-5" data-testid={testId}>
    <h3 className="text-base font-semibold text-gray-800 mb-4">{titulo}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </section>
);

const Casilla = ({ marcada, onCambiar, children, testId, deshabilitada = false }) => (
  <label className={`flex items-center gap-2 text-sm text-gray-700 ${deshabilitada ? 'opacity-50' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      checked={!!marcada}
      disabled={deshabilitada}
      onChange={(e) => onCambiar(e.target.checked)}
      className="h-4 w-4"
      data-testid={testId}
    />
    {children}
  </label>
);

const SeccionPersona = ({ form, cambiar }) => (
  <Seccion titulo="Persona" testId="seccion-persona">
    <Campo etiqueta="Nombre completo (legal)" className="md:col-span-2">
      <input type="text" value={form.nombre_completo} onChange={(e) => cambiar('nombre_completo', e.target.value)} className={CLASE_INPUT} placeholder="Nombre y apellidos" />
    </Campo>
    <Campo etiqueta="Nombre corto *" ayuda="El que sale en la tablet, el fichaje y los eventos">
      <input type="text" value={form.nombre} onChange={(e) => cambiar('nombre', e.target.value)} className={CLASE_INPUT} placeholder="Ej: Juan" data-testid="ficha-nombre" />
    </Campo>
    <Campo etiqueta="DNI / NIE">
      <input type="text" value={form.dni} onChange={(e) => cambiar('dni', e.target.value.toUpperCase())} className={CLASE_INPUT} />
    </Campo>
    <Campo etiqueta="Fecha de nacimiento">
      <input type="date" value={form.fecha_nacimiento} onChange={(e) => cambiar('fecha_nacimiento', e.target.value)} className={CLASE_INPUT} />
    </Campo>
    <Campo etiqueta="Nacionalidad">
      <input type="text" value={form.nacionalidad} onChange={(e) => cambiar('nacionalidad', e.target.value)} className={CLASE_INPUT} placeholder="Española" />
    </Campo>
    <Campo etiqueta="Caducidad del NIE" ayuda="Solo si tiene NIE o permiso de residencia">
      <input type="date" value={form.caducidad_nie} onChange={(e) => cambiar('caducidad_nie', e.target.value)} className={CLASE_INPUT} />
    </Campo>
    <Campo etiqueta="Teléfono">
      <input type="tel" value={form.telefono} onChange={(e) => cambiar('telefono', e.target.value)} className={CLASE_INPUT} />
    </Campo>
    <Campo etiqueta="Email" className="md:col-span-2" ayuda="Con él entra en el portal del empleado">
      <input type="email" value={form.email} onChange={(e) => cambiar('email', e.target.value)} className={CLASE_INPUT} data-testid="ficha-email" />
    </Campo>
    <Campo etiqueta="Dirección" className="md:col-span-2">
      <input type="text" value={form.direccion} onChange={(e) => cambiar('direccion', e.target.value)} className={CLASE_INPUT} placeholder="Calle, número, código postal y localidad" />
    </Campo>
    <div className="md:col-span-2">
      <span className="block text-sm font-medium text-gray-700 mb-1">Contacto de emergencia</span>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input type="text" value={form.ce_nombre} onChange={(e) => cambiar('ce_nombre', e.target.value)} className={CLASE_INPUT} placeholder="Nombre" aria-label="Nombre del contacto de emergencia" />
        <input type="tel" value={form.ce_telefono} onChange={(e) => cambiar('ce_telefono', e.target.value)} className={CLASE_INPUT} placeholder="Teléfono" aria-label="Teléfono del contacto de emergencia" />
        <input type="text" value={form.ce_relacion} onChange={(e) => cambiar('ce_relacion', e.target.value)} className={CLASE_INPUT} placeholder="Relación (pareja, madre...)" aria-label="Relación del contacto de emergencia" />
      </div>
    </div>
  </Seccion>
);

const SeccionOperativa = ({ form, cambiar }) => (
  <Seccion titulo="Operativa" testId="seccion-operativa">
    <div className="md:col-span-2">
      <span className="block text-sm font-medium text-gray-700 mb-1">Color identificativo</span>
      <div className="flex flex-wrap gap-2">
        {COLORES.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => cambiar('color', color.value)}
            className={`w-8 h-8 rounded-full ${color.bg} ${form.color === color.value ? 'ring-2 ring-offset-2 ring-gray-800' : ''} transition-all hover:scale-110`}
            title={color.label}
            aria-label={color.label}
          />
        ))}
        {form.color && (
          <button
            type="button"
            onClick={() => cambiar('color', '')}
            className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-300"
            title="Sin color"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
    <Campo etiqueta="Coste en eventos" ayuda="Cuando va a un evento como responsable del montaje. Por defecto, dirección sin coste y el resto con la tarifa propia.">
      <select
        value={costePersonalEvento(form)}
        onChange={(e) => cambiar('coste_personal_evento', e.target.value)}
        className={CLASE_INPUT}
        data-testid="employee-coste-evento-select"
      >
        <option value="tarifa">Tarifa propia</option>
        <option value="sin_coste">Sin coste (socio)</option>
      </select>
    </Campo>
    <Campo etiqueta="Talla de uniforme">
      <input type="text" value={form.talla_uniforme} onChange={(e) => cambiar('talla_uniforme', e.target.value.toUpperCase())} className={CLASE_INPUT} placeholder="S, M, L, XL..." />
    </Campo>
    <div className="md:col-span-2 flex flex-wrap gap-x-6 gap-y-2">
      <Casilla marcada={!!form.no_va_a_eventos} onCambiar={(v) => cambiar('no_va_a_eventos', v)} testId="employee-no-va-eventos">
        No va a eventos (personal de nave)
      </Casilla>
      <Casilla marcada={esCuentaTecnica(form)} onCambiar={(v) => cambiar('es_cuenta_tecnica', v)} testId="employee-cuenta-tecnica">
        Cuenta técnica (solo para entrar al ERP, no es una persona: fuera de nóminas, obligaciones y eventos)
      </Casilla>
      <Casilla marcada={form.carnet_conducir} onCambiar={(v) => cambiar('carnet_conducir', v)} testId="ficha-carnet-conducir">
        Tiene carné de conducir
      </Casilla>
    </div>
  </Seccion>
);

// Rol con alta y borrado de roles propios (los de sistema no se borran)
const SelectorRol = ({ valor, onCambiar, roles, setRoles }) => {
  const [abierto, setAbierto] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [error, setError] = useState('');
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState('');

  const anadir = async () => {
    const nombre = nombreNuevo.trim();
    if (!nombre) return;
    setError('');
    try {
      const res = await api.post('/roles', { display_name: nombre });
      setRoles((prev) => [...prev, res.data]);
      onCambiar(res.data.role_id);
      setNombreNuevo('');
      setAbierto(false);
    } catch (e) {
      setError(textoErrorApi(e.response?.data?.detail, 'Error al crear el rol'));
    }
  };

  const borrar = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    setErrorBorrar('');
    try {
      await api.delete(`/roles/${aBorrar.role_id}`);
      setRoles((prev) => prev.filter((r) => r.role_id !== aBorrar.role_id));
      setABorrar(null);
    } catch (e) {
      setErrorBorrar(textoErrorApi(e.response?.data?.detail, 'Error al eliminar el rol'));
    } finally {
      setBorrando(false);
    }
  };

  const propios = roles.filter((r) => !r.is_system);

  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1">Rol</span>
      <div className="flex gap-2">
        <select
          data-testid="employee-role-select"
          value={valor}
          onChange={(e) => onCambiar(e.target.value)}
          className={`flex-1 ${CLASE_INPUT}`}
        >
          {roles.map((r) => (
            <option key={r.role_id} value={r.role_id}>{r.display_name}</option>
          ))}
          {valor && !roles.find((r) => r.role_id === valor) && <option value={valor}>{valor}</option>}
        </select>
        <button
          type="button"
          data-testid="add-role-btn"
          onClick={() => setAbierto(!abierto)}
          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg"
          title="Gestionar roles"
        >
          <Plus size={16} />
        </button>
      </div>
      {abierto && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border space-y-2">
          <div className="flex gap-2">
            <input
              data-testid="new-role-name-input"
              type="text"
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              placeholder="Nombre del nuevo rol..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); anadir(); } }}
            />
            <button type="button" data-testid="confirm-add-role-btn" onClick={anadir} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
              Añadir
            </button>
          </div>
          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
          {propios.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {propios.map((r) => (
                <span key={r.role_id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white border rounded-full">
                  {r.display_name}
                  <button
                    type="button"
                    data-testid={`delete-role-${r.role_id}`}
                    onClick={() => { setErrorBorrar(''); setABorrar(r); }}
                    className="text-red-400 hover:text-red-600"
                    title="Eliminar rol"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <DialogoConfirmar
        abierto={!!aBorrar}
        titulo={aBorrar ? `Eliminar el rol ${aBorrar.display_name}` : ''}
        descripcion="Solo se puede si ningún empleado lo tiene asignado."
        textoConfirmar="Eliminar"
        peligro
        ocupado={borrando}
        error={errorBorrar}
        onConfirmar={borrar}
        onCancelar={() => { setABorrar(null); setErrorBorrar(''); }}
        testId="dialogo-borrar-rol"
      />
    </div>
  );
};

const SeccionContrato = ({ form, cambiar, roles, setRoles }) => {
  const antiguedad = antiguedadTexto(form.fecha_antiguedad || form.fecha_alta);
  return (
    <Seccion titulo="Contrato" testId="seccion-contrato">
      <SelectorRol valor={form.rol} onCambiar={(v) => cambiar('rol', v)} roles={roles} setRoles={setRoles} />
      <Campo etiqueta="Tipo de contrato">
        <select value={form.tipo_contrato} onChange={(e) => cambiar('tipo_contrato', e.target.value)} className={CLASE_INPUT} data-testid="ficha-tipo-contrato">
          <option value="">Sin indicar</option>
          {TIPOS_CONTRATO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </Campo>
      <Campo etiqueta="Fecha de alta *">
        <input type="date" value={form.fecha_alta} onChange={(e) => cambiar('fecha_alta', e.target.value)} className={CLASE_INPUT} data-testid="ficha-fecha-alta" />
      </Campo>
      <Campo etiqueta="Fecha de antigüedad" ayuda={antiguedad ? `Antigüedad: ${antiguedad}${form.fecha_antiguedad ? '' : ' (desde el alta)'}` : 'Si es distinta de la fecha de alta'}>
        <input type="date" value={form.fecha_antiguedad} onChange={(e) => cambiar('fecha_antiguedad', e.target.value)} className={CLASE_INPUT} />
      </Campo>
      <Campo etiqueta="Fin de contrato" ayuda="Solo en contratos temporales o de prácticas">
        <input type="date" value={form.fecha_fin_contrato} onChange={(e) => cambiar('fecha_fin_contrato', e.target.value)} className={CLASE_INPUT} />
      </Campo>
      <Campo etiqueta="Jornada">
        <select value={form.jornada} onChange={(e) => cambiar('jornada', e.target.value)} className={CLASE_INPUT}>
          <option value="">Sin indicar</option>
          {JORNADAS.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
        </select>
      </Campo>
      <Campo etiqueta="Horas por semana" ayuda="Para el control del fichaje">
        <input type="number" step="0.5" min="0" max="60" value={form.horas_contrato_semana} onChange={(e) => cambiar('horas_contrato_semana', e.target.value)} className={CLASE_INPUT} />
      </Campo>
      <Campo etiqueta="Categoría del convenio">
        <input type="text" value={form.categoria_convenio} onChange={(e) => cambiar('categoria_convenio', e.target.value)} className={CLASE_INPUT} placeholder="Ej: Ayudante de cocina" />
      </Campo>
      <Campo etiqueta="Días de vacaciones al año" ayuda="Por defecto 30 días naturales">
        <input type="number" min="0" max="60" value={form.dias_vacaciones_anuales} onChange={(e) => cambiar('dias_vacaciones_anuales', e.target.value)} className={CLASE_INPUT} />
      </Campo>
    </Seccion>
  );
};

const SeccionNomina = ({ form, cambiar }) => (
  <Seccion titulo="Nómina" testId="seccion-nomina">
    <Campo etiqueta="Salario bruto mensual (€)">
      <input type="number" step="0.01" min="0" value={form.salario_bruto} onChange={(e) => cambiar('salario_bruto', e.target.value)} className={CLASE_INPUT} data-testid="ficha-salario-bruto" />
    </Campo>
    <Campo etiqueta="Retención IRPF (%)" ayuda="Para el IRPF del resumen fiscal (modelo 111). Vacío: estimación de Finanzas">
      <input type="number" step="0.1" min="0" max="50" value={form.retencion_irpf} onChange={(e) => cambiar('retencion_irpf', e.target.value)} className={CLASE_INPUT} />
    </Campo>
    <Campo etiqueta="Nº de la Seguridad Social">
      <input type="text" value={form.numero_ss} onChange={(e) => cambiar('numero_ss', e.target.value)} className={CLASE_INPUT} placeholder="28/12345678/90" />
    </Campo>
    <Campo etiqueta="IBAN" ayuda="Cuenta donde cobra la nómina">
      <input type="text" value={form.iban} onChange={(e) => cambiar('iban', e.target.value.toUpperCase())} className={CLASE_INPUT} placeholder="ES00 0000 0000 0000 0000 0000" />
    </Campo>
    <div className="md:col-span-2">
      <Casilla marcada={form.exento_ss_empresa} onCambiar={(v) => cambiar('exento_ss_empresa', v)} testId="ficha-exento-ss">
        Exento de Seguridad Social a cargo de la empresa
      </Casilla>
      <p className="text-xs text-gray-500 mt-1 ml-6">Finanzas no le suma la cuota patronal al coste de su nómina.</p>
    </div>
    <div className="md:col-span-2">
      <Casilla marcada={form.sin_nomina} onCambiar={(v) => cambiar('sin_nomina', v)} testId="ficha-sin-nomina">
        No cobra por nómina
      </Casilla>
      <p className="text-xs text-gray-500 mt-1 ml-6">Por ejemplo, un socio con otra retribución. La remesa de nóminas no espera su nómina de a3doc.</p>
    </div>
  </Seccion>
);

// Avisos del servidor (DNI, IBAN, nº SS...): no bloquean el guardado
const AvisosServidor = ({ avisos, onCerrar }) => {
  if (!avisos || avisos.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm" role="status" data-testid="ficha-avisos-servidor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium">Guardado, pero revisa estos datos:</p>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            {avisos.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </div>
        {onCerrar && (
          <button type="button" onClick={onCerrar} className="shrink-0 opacity-60 hover:opacity-100" title="Cerrar aviso">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const BloqueError = ({ texto, testId }) => (texto ? (
  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm whitespace-pre-line" role="alert" data-testid={testId}>
    {texto}
  </div>
) : null);

// ==================== PESTAÑA OBLIGACIONES ====================

const PestanaObligaciones = ({ empleado, obligaciones, error, puedeGestionar, onCambio, mostrarAviso, onIrAPestana }) => {
  const [editando, setEditando] = useState(null);
  const [formObligacion, setFormObligacion] = useState({ fecha: '', caduca: '', nota: '' });
  const [guardando, setGuardando] = useState(false);
  const manuales = empleado?.obligaciones || {};
  const plantillasPorTipo = obligaciones?.plantillas_disponibles || {};

  // Visor: { titulo, url, driveId }
  const [visor, setVisor] = useState(null);
  // Enviar al empleado: { item, plantillas }
  const [envio, setEnvio] = useState(null);
  const [envioPlantilla, setEnvioPlantilla] = useState('');
  const [envioLimite, setEnvioLimite] = useState('');
  // Validar o rechazar: { tipo: 'validar'|'rechazar', item, asignacion }
  const [revision, setRevision] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');

  const abrirEnvio = (item, plantillas) => {
    setErrorDialogo('');
    setEnvioPlantilla(plantillas.length === 1 ? plantillas[0].id : '');
    setEnvioLimite('');
    setEnvio({ item, plantillas });
  };

  const confirmarEnvio = async () => {
    if (!envio || !envioPlantilla) return;
    setOcupado(true);
    setErrorDialogo('');
    try {
      await gestionDocumentalAPI.asignar(envioPlantilla, [empleado.id], envioLimite);
      setEnvio(null);
      mostrarAviso('ok', 'Documento enviado al empleado');
      await onCambio();
    } catch (e) {
      console.error('Error enviando documento:', e);
      setErrorDialogo(textoErrorApi(e.response?.data?.detail, 'No se ha podido enviar el documento'));
    } finally {
      setOcupado(false);
    }
  };

  const abrirRevision = (tipo, item) => {
    setErrorDialogo('');
    setMotivoRechazo('');
    setRevision({ tipo, item, asignacion: item.asignacion });
  };

  const confirmarRevision = async () => {
    if (!revision) return;
    if (revision.tipo === 'rechazar' && !motivoRechazo.trim()) return;
    setOcupado(true);
    setErrorDialogo('');
    try {
      if (revision.tipo === 'validar') {
        await gestionDocumentalAPI.validar(revision.asignacion.id);
      } else {
        await gestionDocumentalAPI.rechazar(revision.asignacion.id, motivoRechazo.trim());
      }
      const texto = revision.tipo === 'validar' ? 'Documento validado' : 'Documento rechazado: vuelve al empleado como pendiente';
      setRevision(null);
      mostrarAviso('ok', texto);
      await onCambio();
    } catch (e) {
      console.error('Error revisando documento:', e);
      setErrorDialogo(textoErrorApi(e.response?.data?.detail, revision.tipo === 'validar' ? 'No se ha podido validar' : 'No se ha podido rechazar'));
    } finally {
      setOcupado(false);
    }
  };

  const verDocumento = (item) => {
    const asig = item.asignacion;
    if (!asig) return;
    const firmado = asig.archivo_firmado_url || asig.drive_file_id;
    if (firmado) {
      setVisor({
        titulo: `${asig.nombre_documento || item.texto} (firmado)`,
        url: asig.archivo_firmado_url || null,
        driveId: asig.drive_file_id || null,
      });
    } else {
      setVisor({ titulo: asig.nombre_documento || item.texto, url: asig.archivo_original_url || null });
    }
  };

  // Ver una plantilla antes de enviarla (desde el nombre del punto o desde el modal de envío)
  const verPlantilla = (pl, item) => {
    if (!pl?.archivo_url) return;
    setVisor({ titulo: pl.nombre || item?.texto || 'Documento', url: pl.archivo_url });
  };
  const verAntesDeEnviar = (item, plantillas) => {
    if (item.asignacion && (item.asignacion.archivo_original_url || item.asignacion.archivo_firmado_url || item.asignacion.drive_file_id)) {
      verDocumento(item);
    } else if (plantillas.length === 1) {
      verPlantilla(plantillas[0], item);
    } else if (plantillas.length > 1) {
      abrirEnvio(item, plantillas);
    }
  };

  const abrir = (clave) => {
    setEditando(clave);
    setFormObligacion({ fecha: hoyMadrid(), caduca: '', nota: '' });
  };

  const guardar = async (clave, hecho) => {
    setGuardando(true);
    try {
      const body = { hecho };
      if (hecho) {
        body.fecha = formObligacion.fecha || hoyMadrid();
        if (formObligacion.caduca) body.caduca = formObligacion.caduca;
        if (formObligacion.nota.trim()) body.nota = formObligacion.nota.trim();
      }
      await obligacionesAPI.marcar(empleado.id, clave, body);
      setEditando(null);
      await onCambio();
    } catch (e) {
      console.error('Error guardando obligación:', e);
      mostrarAviso('error', textoErrorApi(e.response?.data?.detail, 'Error al guardar'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="obligaciones-empleado">
      <BloqueError texto={error} />
      {obligaciones && (
        <div className={`rounded-lg p-4 border text-sm ${obligaciones.faltan > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {obligaciones.faltan > 0
            ? `Le ${obligaciones.faltan === 1 ? 'falta 1 punto' : `faltan ${obligaciones.faltan} puntos`} para estar en regla.`
            : 'Está en regla: no le falta nada.'}
        </div>
      )}
      {!obligaciones && !error && <p className="text-gray-500 text-sm py-4 text-center">Cargando...</p>}
      {(obligaciones?.items || []).map((item) => {
        const marca = marcaObligacion(item);
        const manual = manuales[item.clave];
        const hechoAMano = !!manual?.hecho;
        const abierto = editando === item.clave;
        const asig = item.tipo_documental ? (item.asignacion || null) : null;
        const plantillasTipo = item.tipo_documental ? (plantillasPorTipo[item.tipo_documental] || []) : [];
        // Enviar: solo gestión, punto con documento, sin hacer y sin una asignación en curso
        const puedeEnviar = puedeGestionar && !!item.tipo_documental && item.ok !== true
          && !(asig && ESTADOS_ASIGNACION_VIVA.includes(asig.estado));
        return (
          <div key={item.clave} data-testid={`obligacion-${item.clave}`} className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${marca.clase}`}>{marca.texto}</span>
                <div className="min-w-0">
                  {(asig && (asig.archivo_original_url || asig.archivo_firmado_url || asig.drive_file_id)) || plantillasTipo.some((pl) => pl.archivo_url) ? (
                    <button
                      type="button"
                      onClick={() => verAntesDeEnviar(item, plantillasTipo)}
                      className="font-medium text-gray-900 text-left hover:text-duetto-primary hover:underline"
                      title="Ver el documento"
                      data-testid={`obligacion-nombre-${item.clave}`}
                    >
                      {item.texto}
                    </button>
                  ) : (
                    <p className="font-medium text-gray-900">{item.texto}</p>
                  )}
                  {item.detalle && <p className="text-xs text-gray-500 mt-0.5">{item.detalle}</p>}
                  {item.aviso && <p className="text-xs text-gray-500 mt-0.5">{item.aviso}</p>}
                  {/* El IBAN lo pone el empleado desde Mi Inicio; gestión lo edita en Contrato y nómina */}
                  {item.accion_empleado === 'iban' && puedeGestionar && onIrAPestana && (
                    <button
                      type="button"
                      onClick={() => onIrAPestana('contrato')}
                      className="text-xs text-duetto-primary hover:underline mt-0.5"
                      data-testid="obligacion-iban-editar"
                    >
                      Editar en la ficha
                    </button>
                  )}
                  {hechoAMano && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Hecho el {fechaCorta(manual.fecha)}{manual.por ? ` por ${manual.por}` : ''}
                      {manual.caduca ? `, caduca el ${fechaCorta(manual.caduca)}` : ''}
                      {manual.nota ? `. ${manual.nota}` : ''}
                    </p>
                  )}
                  {asig && (
                    <p
                      className={`text-xs mt-0.5 ${asig.notas_rechazo && (asig.estado === 'Pendiente' || asig.estado === 'Rechazado') ? 'text-red-700' : 'text-indigo-700'}`}
                      data-testid={`obligacion-estado-doc-${item.clave}`}
                    >
                      {asig.nombre_documento ? `${asig.nombre_documento}: ` : ''}{estadoAsignacionTexto(asig)}
                    </p>
                  )}
                  {puedeEnviar && plantillasTipo.length === 0 && (
                    <p className="text-xs text-gray-500 mt-0.5" data-testid={`obligacion-sin-plantilla-${item.clave}`}>
                      Sube una plantilla de tipo {nombreTipoDocumental(item.tipo_documental)} en{' '}
                      <Link to="/gestion-documental" className="text-duetto-primary hover:underline">Gestión Documental</Link>
                      {' '}para enviársela al empleado.
                    </p>
                  )}
                </div>
              </div>
              {!abierto && (asig || puedeEnviar) && (
                <div className="flex flex-wrap gap-2 shrink-0">
                  {asig && (asig.archivo_firmado_url || asig.drive_file_id || asig.archivo_original_url) && (
                    <button
                      type="button"
                      onClick={() => verDocumento(item)}
                      className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      data-testid={`obligacion-ver-${item.clave}`}
                    >
                      {asig.archivo_firmado_url || asig.drive_file_id ? 'Ver firmado' : 'Ver documento'}
                    </button>
                  )}
                  {puedeGestionar && asig?.estado === 'Firmado' && (
                    <>
                      <button
                        type="button"
                        onClick={() => abrirRevision('validar', item)}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                        data-testid={`obligacion-validar-${item.clave}`}
                      >
                        Validar
                      </button>
                      <button
                        type="button"
                        onClick={() => abrirRevision('rechazar', item)}
                        className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
                        data-testid={`obligacion-rechazar-${item.clave}`}
                      >
                        Rechazar
                      </button>
                    </>
                  )}
                  {puedeEnviar && plantillasTipo.length > 0 && (
                    <button
                      type="button"
                      onClick={() => abrirEnvio(item, plantillasTipo)}
                      className="px-3 py-1.5 text-sm border border-duetto-primary text-duetto-primary rounded-lg hover:bg-duetto-primary hover:text-white"
                      data-testid={`obligacion-enviar-${item.clave}`}
                    >
                      Enviar al empleado
                    </button>
                  )}
                </div>
              )}
              {puedeGestionar && item.manual_permitido && !abierto && (
                <div className="flex gap-2 shrink-0">
                  {item.ok !== true && (
                    <button
                      type="button"
                      onClick={() => abrir(item.clave)}
                      className="px-3 py-1.5 text-sm bg-duetto-primary text-white rounded-lg hover:bg-opacity-90"
                      data-testid={`obligacion-marcar-${item.clave}`}
                    >
                      Marcar hecho
                    </button>
                  )}
                  {hechoAMano && (
                    <button
                      type="button"
                      onClick={() => guardar(item.clave, false)}
                      disabled={guardando}
                      className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      data-testid={`obligacion-deshacer-${item.clave}`}
                    >
                      Deshacer
                    </button>
                  )}
                </div>
              )}
            </div>
            {abierto && (
              <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Campo etiqueta="Fecha">
                  <input type="date" value={formObligacion.fecha} onChange={(e) => setFormObligacion({ ...formObligacion, fecha: e.target.value })} className={CLASE_INPUT} />
                </Campo>
                <Campo etiqueta="Caduca (opcional)" ayuda={item.clave === 'reconocimiento_medico' ? 'Si no indicas nada, caduca en 12 meses' : undefined}>
                  <input type="date" value={formObligacion.caduca} onChange={(e) => setFormObligacion({ ...formObligacion, caduca: e.target.value })} className={CLASE_INPUT} />
                </Campo>
                <Campo etiqueta="Nota (opcional)">
                  <input type="text" value={formObligacion.nota} onChange={(e) => setFormObligacion({ ...formObligacion, nota: e.target.value })} className={CLASE_INPUT} />
                </Campo>
                <div className="sm:col-span-3 flex justify-end gap-2">
                  <button type="button" onClick={() => setEditando(null)} className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => guardar(item.clave, true)}
                    disabled={guardando || !formObligacion.fecha}
                    className="px-3 py-1.5 text-sm bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50"
                    data-testid={`obligacion-guardar-${item.clave}`}
                  >
                    {guardando ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <DialogoConfirmar
        abierto={!!envio}
        titulo="Enviar documento al empleado"
        descripcion={envio ? `${empleado?.nombre || 'El empleado'} lo verá en su portal (pestaña Firmas) para descargarlo, firmarlo y subirlo.` : ''}
        textoConfirmar="Enviar"
        ocupado={ocupado}
        deshabilitado={!envioPlantilla}
        error={errorDialogo}
        onConfirmar={confirmarEnvio}
        onCancelar={() => setEnvio(null)}
        testId="dialogo-enviar-documento"
      >
        {envio && (
          <>
            <Campo etiqueta="Documento">
              {envio.plantillas.length === 1 ? (
                <p className="text-sm text-gray-800">
                  {envio.plantillas[0].nombre}
                  {envio.plantillas[0].archivo_url && (
                    <a href={urlConToken(envio.plantillas[0].archivo_url, '&inline=1')} target="_blank" rel="noopener noreferrer" className="ml-2 text-duetto-primary hover:underline" data-testid="enviar-documento-ver">Ver en pestaña nueva</a>
                  )}
                </p>
              ) : (
                <select value={envioPlantilla} onChange={(e) => setEnvioPlantilla(e.target.value)} className={CLASE_INPUT} data-testid="enviar-documento-plantilla">
                  <option value="">Elige la plantilla...</option>
                  {envio.plantillas.map((pl) => (
                    <option key={pl.id} value={pl.id}>{pl.nombre}</option>
                  ))}
                </select>
              )}
              {envio.plantillas.length > 1 && envioPlantilla && (envio.plantillas.find((pl) => pl.id === envioPlantilla) || {}).archivo_url && (
                <a href={urlConToken((envio.plantillas.find((pl) => pl.id === envioPlantilla) || {}).archivo_url, '&inline=1')} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-sm text-duetto-primary hover:underline" data-testid="enviar-documento-ver">Ver en pestaña nueva</a>
              )}
            </Campo>
            <Campo etiqueta="Fecha límite (opcional)">
              <input type="date" value={envioLimite} onChange={(e) => setEnvioLimite(e.target.value)} className={CLASE_INPUT} data-testid="enviar-documento-limite" />
            </Campo>
          </>
        )}
      </DialogoConfirmar>

      <DialogoConfirmar
        abierto={!!revision}
        titulo={revision?.tipo === 'validar' ? 'Validar documento firmado' : 'Rechazar documento firmado'}
        descripcion={revision
          ? (revision.tipo === 'validar'
            ? `El punto "${revision.item.texto}" quedará hecho.`
            : 'El documento vuelve al empleado como pendiente y verá el motivo en su portal.')
          : ''}
        textoConfirmar={revision?.tipo === 'validar' ? 'Validar' : 'Rechazar'}
        peligro={revision?.tipo === 'rechazar'}
        ocupado={ocupado}
        deshabilitado={revision?.tipo === 'rechazar' && !motivoRechazo.trim()}
        error={errorDialogo}
        onConfirmar={confirmarRevision}
        onCancelar={() => setRevision(null)}
        testId="dialogo-revisar-documento"
      >
        {revision?.tipo === 'rechazar' && (
          <Campo etiqueta="Motivo del rechazo">
            <textarea
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              rows={3}
              className={CLASE_INPUT}
              placeholder="Ej.: falta la firma en la página 2"
              data-testid="rechazar-documento-motivo"
            />
          </Campo>
        )}
      </DialogoConfirmar>

      <VisorDocumento
        abierto={!!visor}
        onCerrar={() => setVisor(null)}
        titulo={visor?.titulo}
        url={visor?.url || undefined}
        driveId={visor?.driveId || undefined}
      />
    </div>
  );
};

// ==================== PESTAÑA DOCUMENTOS Y FORMACIÓN ====================

const DOC_VACIO = { tipo: '', nombre: '', descripcion: '', archivo_url: '', fecha_documento: '' };
const CURSO_VACIO = { nombre_curso: '', descripcion: '', fecha_completado: '', valido_hasta: '', certificado_url: '', horas: '', impartido_por: '' };

const Superpuesto = ({ titulo, children, pie }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
      <h3 className="text-xl font-bold mb-4">{titulo}</h3>
      <div className="space-y-3">{children}</div>
      <div className="flex gap-3 mt-6">{pie}</div>
    </div>
  </div>
);

const PestanaDocumentos = ({ empleadoId, mostrarAviso }) => {
  const [documentos, setDocumentos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');

  const [docAbierto, setDocAbierto] = useState(false);
  const [nuevoDoc, setNuevoDoc] = useState(DOC_VACIO);
  const [ficheroDoc, setFicheroDoc] = useState(null);
  const [subiendoDoc, setSubiendoDoc] = useState(false);
  const [errorDoc, setErrorDoc] = useState('');

  const [cursoAbierto, setCursoAbierto] = useState(false);
  const [nuevoCurso, setNuevoCurso] = useState(CURSO_VACIO);
  const [ficheroCurso, setFicheroCurso] = useState(null);
  const [guardandoCurso, setGuardandoCurso] = useState(false);
  const [errorCurso, setErrorCurso] = useState('');
  const [avisoCursos, setAvisoCursos] = useState('');

  // Visor de documentos: { titulo, url }
  const [visorDoc, setVisorDoc] = useState(null);

  // Borrado de documento o curso: { tipo: 'documento'|'curso', id, nombre }
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState('');

  const cargarDocumentos = useCallback(async () => {
    const res = await empleadosAPI.getDocumentos(empleadoId);
    setDocumentos(res.data || []);
  }, [empleadoId]);

  const cargarCursos = useCallback(async () => {
    const res = await empleadosAPI.getCursos(empleadoId);
    setCursos(res.data || []);
  }, [empleadoId]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setErrorCarga('');
    Promise.all([cargarDocumentos(), cargarCursos()])
      .catch((e) => {
        console.error('Error cargando documentos y cursos:', e);
        if (vivo) setErrorCarga(textoErrorApi(e.response?.data?.detail, 'No se han podido cargar los documentos y cursos'));
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [cargarDocumentos, cargarCursos]);

  const elegirFicheroDoc = (e) => {
    const fichero = e.target.files[0];
    if (!fichero) return;
    if (fichero.size > 10 * 1024 * 1024) {
      setErrorDoc('El archivo es demasiado grande. Máximo 10 MB');
      return;
    }
    setErrorDoc('');
    setFicheroDoc(fichero);
    setNuevoDoc((prev) => (prev.nombre ? prev : { ...prev, nombre: fichero.name }));
  };

  const cerrarDoc = () => {
    setDocAbierto(false);
    setFicheroDoc(null);
    setNuevoDoc(DOC_VACIO);
    setErrorDoc('');
  };

  const anadirDocumento = async () => {
    if (!nuevoDoc.tipo) {
      setErrorDoc('Selecciona un tipo de documento');
      return;
    }
    setErrorDoc('');
    setSubiendoDoc(true);
    try {
      if (ficheroDoc) {
        const datos = new FormData();
        datos.append('file', ficheroDoc);
        datos.append('tipo', nuevoDoc.tipo);
        datos.append('nombre', nuevoDoc.nombre);
        datos.append('descripcion', nuevoDoc.descripcion);
        if (nuevoDoc.fecha_documento) datos.append('fecha_documento', nuevoDoc.fecha_documento);
        await empleadosAPI.uploadDocumento(empleadoId, datos);
      } else {
        await empleadosAPI.addDocumento(empleadoId, {
          empleado_id: empleadoId,
          tipo: nuevoDoc.tipo,
          nombre: nuevoDoc.nombre,
          descripcion: nuevoDoc.descripcion || null,
          archivo_url: nuevoDoc.archivo_url || null,
          fecha_documento: nuevoDoc.fecha_documento || null,
        });
      }
      await cargarDocumentos();
      cerrarDoc();
      mostrarAviso('ok', 'Documento añadido');
    } catch (e) {
      console.error('Error añadiendo documento:', e);
      setErrorDoc(textoErrorApi(e.response?.data?.detail, e.response?.data?.message || e.message || 'Error al añadir el documento'));
    } finally {
      setSubiendoDoc(false);
    }
  };

  const cerrarCurso = () => {
    setCursoAbierto(false);
    setFicheroCurso(null);
    setNuevoCurso(CURSO_VACIO);
    setErrorCurso('');
  };

  const anadirCurso = async () => {
    setAvisoCursos('');
    setErrorCurso('');
    setGuardandoCurso(true);
    try {
      const datos = { empleado_id: empleadoId, nombre_curso: nuevoCurso.nombre_curso, fecha_completado: nuevoCurso.fecha_completado };
      if (nuevoCurso.descripcion) datos.descripcion = nuevoCurso.descripcion;
      if (nuevoCurso.horas) datos.horas = parseInt(nuevoCurso.horas, 10);
      if (nuevoCurso.valido_hasta) datos.valido_hasta = nuevoCurso.valido_hasta;
      if (nuevoCurso.certificado_url) datos.certificado_url = nuevoCurso.certificado_url;
      if (nuevoCurso.impartido_por) datos.impartido_por = nuevoCurso.impartido_por;
      const res = await empleadosAPI.addCurso(empleadoId, datos);
      // Certificado adjunto: si falla la subida, el curso queda guardado y se avisa
      if (ficheroCurso && res.data?.id) {
        const fd = new FormData();
        fd.append('file', ficheroCurso);
        try {
          await empleadosAPI.uploadCertificadoCurso(empleadoId, res.data.id, fd);
        } catch (errorSubida) {
          console.error('Error subiendo el certificado:', errorSubida);
          setAvisoCursos(`El curso se ha guardado, pero el certificado no se ha podido subir: ${textoErrorApi(errorSubida.response?.data?.detail, errorSubida.message || 'error desconocido')}`);
        }
      }
      await cargarCursos();
      cerrarCurso();
      mostrarAviso('ok', 'Curso añadido');
    } catch (e) {
      console.error('Error añadiendo curso:', e);
      setErrorCurso(`Error al añadir el curso: ${textoErrorApi(e.response?.data?.detail, e.message)}`);
    } finally {
      setGuardandoCurso(false);
    }
  };

  const confirmarBorrado = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    setErrorBorrar('');
    try {
      if (aBorrar.tipo === 'documento') {
        await empleadosAPI.deleteDocumento(empleadoId, aBorrar.id);
        await cargarDocumentos();
      } else {
        await empleadosAPI.deleteCurso(empleadoId, aBorrar.id);
        await cargarCursos();
      }
      mostrarAviso('ok', aBorrar.tipo === 'documento' ? 'Documento eliminado' : 'Curso eliminado');
      setABorrar(null);
    } catch (e) {
      console.error('Error eliminando:', e);
      setErrorBorrar(textoErrorApi(e.response?.data?.detail, aBorrar.tipo === 'documento' ? 'Error al eliminar el documento' : 'Error al eliminar el curso'));
    } finally {
      setBorrando(false);
    }
  };

  if (cargando) return <p className="text-center py-8 text-gray-500">Cargando...</p>;

  const hoy = hoyMadrid();

  return (
    <div className="space-y-6">
      <BloqueError texto={errorCarga} />

      {/* Documentos */}
      <section className="space-y-3" data-testid="ficha-documentos">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-800">Documentos</h3>
          <button type="button" onClick={() => setDocAbierto(true)} className="bg-duetto-primary text-white px-3 py-2 rounded-lg hover:bg-opacity-90 flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Añadir documento
          </button>
        </div>
        {documentos.length === 0 ? (
          <p className="text-gray-500 text-sm py-4 text-center bg-gray-50 rounded-lg">No hay documentos</p>
        ) : (
          <div className="space-y-2">
            {documentos.map((doc) => {
              const tipo = TIPOS_DOCUMENTO.find((t) => t.value === doc.tipo)?.label || doc.tipo;
              return (
                <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">{tipo}</span>
                      <span className="font-semibold text-gray-900 break-all">{doc.nombre}</span>
                    </div>
                    {doc.descripcion && <p className="text-sm text-gray-600">{doc.descripcion}</p>}
                    <div className="flex flex-wrap gap-x-4 text-xs text-gray-500 mt-1">
                      {doc.fecha_documento && <span>Fecha: {fechaLarga(doc.fecha_documento)}</span>}
                      {doc.archivo_url && (
                        <>
                          <button
                            type="button"
                            onClick={() => setVisorDoc({ titulo: doc.nombre, url: doc.archivo_url })}
                            className="text-duetto-primary hover:underline font-medium"
                            data-testid={`documento-ver-${doc.id}`}
                          >
                            Ver documento
                          </button>
                          <a href={urlConToken(doc.archivo_url, '&inline=true')} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:underline">
                            Abrir en pestaña
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={() => { setErrorBorrar(''); setABorrar({ tipo: 'documento', id: doc.id, nombre: doc.nombre }); }} className="text-red-600 hover:text-red-800 p-2" title="Eliminar documento">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Cursos y formación */}
      <section className="space-y-3" data-testid="ficha-cursos">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-800">Cursos y formación</h3>
          <button type="button" onClick={() => setCursoAbierto(true)} className="bg-duetto-primary text-white px-3 py-2 rounded-lg hover:bg-opacity-90 flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Añadir curso
          </button>
        </div>
        <BloqueError texto={avisoCursos} testId="cursos-aviso" />
        {cursos.length === 0 ? (
          <p className="text-gray-500 text-sm py-4 text-center bg-gray-50 rounded-lg">No hay cursos registrados</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cursos.map((curso) => {
              const caducado = !!curso.valido_hasta && fecha10(curso.valido_hasta) < hoy;
              return (
                <div key={curso.id} className={`border-2 rounded-lg p-4 ${caducado ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Award className={`w-5 h-5 shrink-0 ${caducado ? 'text-red-600' : 'text-green-600'}`} />
                      <span className="font-bold text-gray-900">{curso.nombre_curso}</span>
                    </div>
                    <button type="button" onClick={() => { setErrorBorrar(''); setABorrar({ tipo: 'curso', id: curso.id, nombre: curso.nombre_curso }); }} className="text-red-600 hover:text-red-800" title="Eliminar curso">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {curso.descripcion && <p className="text-sm text-gray-700 mb-2">{curso.descripcion}</p>}
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>Completado: {fechaLarga(curso.fecha_completado)}</p>
                    {curso.valido_hasta && (
                      <p className={caducado ? 'text-red-600 font-semibold' : ''}>
                        {caducado ? 'Caducado' : 'Válido hasta'}: {fechaLarga(curso.valido_hasta)}
                      </p>
                    )}
                    {curso.horas ? <p>{curso.horas} horas</p> : null}
                    {curso.impartido_por && <p>Impartido por: {curso.impartido_por}</p>}
                  </div>
                  {(curso.archivo_url || curso.certificado_url) ? (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {curso.archivo_url && (
                        <a href={urlConToken(curso.archivo_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-300 rounded-md text-xs text-duetto-primary hover:bg-gray-50">
                          <Paperclip className="w-3 h-3" /> Ver certificado
                        </a>
                      )}
                      {curso.certificado_url && (
                        <a href={curso.certificado_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-300 rounded-md text-xs text-duetto-primary hover:bg-gray-50">
                          Certificado (enlace)
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-3">Sin certificado adjunto</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {docAbierto && (
        <Superpuesto
          titulo="Añadir documento"
          pie={(
            <>
              <button type="button" onClick={cerrarDoc} disabled={subiendoDoc} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button
                type="button"
                onClick={anadirDocumento}
                disabled={!nuevoDoc.tipo || !nuevoDoc.nombre || subiendoDoc || (!ficheroDoc && !nuevoDoc.archivo_url)}
                className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90 disabled:opacity-50"
              >
                {subiendoDoc ? 'Subiendo...' : 'Añadir'}
              </button>
            </>
          )}
        >
          <Campo etiqueta="Tipo de documento *">
            <select value={nuevoDoc.tipo} onChange={(e) => setNuevoDoc((prev) => ({ ...prev, tipo: e.target.value }))} className={CLASE_INPUT}>
              <option value="" disabled>Selecciona un tipo...</option>
              {TIPOS_DOCUMENTO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Campo>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            <span className="block text-sm font-medium text-gray-700 mb-2">
              <Upload className="w-4 h-4 inline mr-1" /> Subir archivo (PDF, DOC, DOCX, JPG, PNG, máx. 10 MB)
            </span>
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={elegirFicheroDoc} className="w-full text-sm" />
            {ficheroDoc && <p className="text-xs text-green-600 mt-2">Archivo: {ficheroDoc.name}</p>}
          </div>
          <p className="text-center text-xs text-gray-500">o bien</p>
          <Campo etiqueta="Enlace (Google Drive, etc.)">
            <input type="url" value={nuevoDoc.archivo_url} onChange={(e) => setNuevoDoc((prev) => ({ ...prev, archivo_url: e.target.value }))} className={CLASE_INPUT} placeholder="https://drive.google.com/..." disabled={ficheroDoc !== null} />
          </Campo>
          <Campo etiqueta="Nombre *">
            <input type="text" value={nuevoDoc.nombre} onChange={(e) => setNuevoDoc((prev) => ({ ...prev, nombre: e.target.value }))} className={CLASE_INPUT} placeholder="Ej: Contrato 2026" />
          </Campo>
          <Campo etiqueta="Descripción">
            <textarea value={nuevoDoc.descripcion} onChange={(e) => setNuevoDoc((prev) => ({ ...prev, descripcion: e.target.value }))} className={CLASE_INPUT} rows="2" />
          </Campo>
          <Campo etiqueta="Fecha del documento">
            <input type="date" value={nuevoDoc.fecha_documento} onChange={(e) => setNuevoDoc((prev) => ({ ...prev, fecha_documento: e.target.value }))} className={CLASE_INPUT} />
          </Campo>
          <BloqueError texto={errorDoc} />
        </Superpuesto>
      )}

      {cursoAbierto && (
        <Superpuesto
          titulo="Añadir curso"
          pie={(
            <>
              <button type="button" onClick={cerrarCurso} disabled={guardandoCurso} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button
                type="button"
                onClick={anadirCurso}
                disabled={!nuevoCurso.nombre_curso || !nuevoCurso.fecha_completado || guardandoCurso}
                className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90 disabled:opacity-50"
              >
                {guardandoCurso ? 'Guardando...' : 'Añadir'}
              </button>
            </>
          )}
        >
          <Campo etiqueta="Nombre del curso *">
            <input type="text" value={nuevoCurso.nombre_curso} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, nombre_curso: e.target.value }))} className={CLASE_INPUT} placeholder="Ej: Manipulador de alimentos" />
          </Campo>
          <Campo etiqueta="Descripción">
            <textarea value={nuevoCurso.descripcion} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, descripcion: e.target.value }))} className={CLASE_INPUT} rows="2" />
          </Campo>
          <Campo etiqueta="Fecha en que lo completó *">
            <input type="date" value={nuevoCurso.fecha_completado} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, fecha_completado: e.target.value }))} className={CLASE_INPUT} />
          </Campo>
          <Campo etiqueta="Válido hasta (opcional)">
            <input type="date" value={nuevoCurso.valido_hasta} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, valido_hasta: e.target.value }))} className={CLASE_INPUT} />
          </Campo>
          <Campo etiqueta="Horas (opcional)">
            <input type="number" value={nuevoCurso.horas} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, horas: e.target.value }))} className={CLASE_INPUT} placeholder="20" />
          </Campo>
          <Campo etiqueta="Impartido por (opcional)">
            <input type="text" value={nuevoCurso.impartido_por} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, impartido_por: e.target.value }))} className={CLASE_INPUT} placeholder="Institución o empresa" />
          </Campo>
          <Campo etiqueta="Enlace al certificado (opcional)">
            <input type="url" value={nuevoCurso.certificado_url} onChange={(e) => setNuevoCurso((prev) => ({ ...prev, certificado_url: e.target.value }))} className={CLASE_INPUT} placeholder="https://..." />
          </Campo>
          <Campo etiqueta="Adjuntar certificado (opcional)">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setFicheroCurso(e.target.files[0] || null)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            {ficheroCurso && <span className="block text-xs text-green-600 mt-1">Archivo: {ficheroCurso.name}</span>}
          </Campo>
          <BloqueError texto={errorCurso} />
        </Superpuesto>
      )}

      <DialogoConfirmar
        abierto={!!aBorrar}
        titulo={aBorrar?.tipo === 'documento' ? 'Eliminar documento' : 'Eliminar curso'}
        descripcion={aBorrar ? `Se eliminará "${aBorrar.nombre || 'sin nombre'}". No se puede deshacer.` : ''}
        textoConfirmar="Eliminar"
        peligro
        ocupado={borrando}
        error={errorBorrar}
        onConfirmar={confirmarBorrado}
        onCancelar={() => { setABorrar(null); setErrorBorrar(''); }}
        testId="dialogo-borrar-ficha"
      />

      <VisorDocumento
        abierto={!!visorDoc}
        onCerrar={() => setVisorDoc(null)}
        titulo={visorDoc?.titulo}
        url={visorDoc?.url || undefined}
      />
    </div>
  );
};

// ==================== PESTAÑA ACCESO Y PERMISOS ====================

const PestanaAcceso = ({
  empleado, puedeGestionar, permisos, onTogglePermiso, hayCambiosPermisos, onGuardarPermisos, guardandoPermisos,
  refrescarEmpleado, refrescarObligaciones, mostrarAviso, form, cambiar,
}) => {
  const [confirmarInvitacion, setConfirmarInvitacion] = useState(false);
  const [cambiandoTour, setCambiandoTour] = useState(false);
  const { aviso: avisoTour, mostrarAviso: mostrarAvisoTour, limpiarAviso: limpiarAvisoTour } = useAvisoTemporal(6000);
  const [enviando, setEnviando] = useState(false);
  const [enlace, setEnlace] = useState('');
  const [avisoInvitacion, setAvisoInvitacion] = useState('');
  const [errorInvitacion, setErrorInvitacion] = useState('');
  const [verPasswordManual, setVerPasswordManual] = useState(false);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [guardandoPassword, setGuardandoPassword] = useState(false);

  const generarInvitacion = async (enviarEmail) => {
    setEnviando(true);
    setErrorInvitacion('');
    setAvisoInvitacion('');
    try {
      const res = await empleadosAPI.invitacionPortal(empleado.id, enviarEmail);
      const datos = res.data || {};
      if (enviarEmail) {
        setEnlace('');
        setAvisoInvitacion(`Correo enviado a ${datos.enviado_a || empleado.email}, el enlace caduca el ${fechaCorta(datos.caduca_en)}`);
      } else {
        setEnlace(datos.enlace || '');
        setAvisoInvitacion(`Enlace generado, caduca el ${fechaCorta(datos.caduca_en)}. Si generas otro, este deja de valer.`);
      }
      refrescarEmpleado();
    } catch (e) {
      console.error('Error con la invitación al portal:', e);
      setErrorInvitacion(textoErrorApi(e.response?.data?.detail, 'No se ha podido generar la invitación'));
    } finally {
      setEnviando(false);
      setConfirmarInvitacion(false);
    }
  };

  const copiarEnlace = async () => {
    try {
      await navigator.clipboard.writeText(enlace);
      setAvisoInvitacion('Enlace copiado al portapapeles');
    } catch (e) {
      setErrorInvitacion('No se ha podido copiar: selecciona el enlace y cópialo a mano');
    }
  };

  const guardarPassword = async () => {
    if (!nuevaPassword || nuevaPassword.length < 4) {
      mostrarAviso('error', 'La contraseña debe tener al menos 4 caracteres');
      return;
    }
    setGuardandoPassword(true);
    try {
      await empleadosAPI.setPassword(empleado.id, nuevaPassword);
      setNuevaPassword('');
      setVerPasswordManual(false);
      mostrarAviso('ok', 'Contraseña guardada. Díselo al empleado por un canal seguro.');
      await refrescarEmpleado();
    } catch (e) {
      console.error('Error guardando contraseña:', e);
      mostrarAviso('error', textoErrorApi(e.response?.data?.detail, 'Error al guardar la contraseña'));
    } finally {
      setGuardandoPassword(false);
    }
  };

  // Tour de bienvenida: se enseña una vez al entrar en Mi Inicio
  const cambiarTour = async (pendiente) => {
    setCambiandoTour(true);
    limpiarAvisoTour();
    try {
      await empleadosAPI.tourBienvenida(empleado.id, pendiente);
      mostrarAvisoTour('ok', pendiente
        ? 'Tour activado: lo verá la próxima vez que entre en Mi Inicio'
        : 'Tour desactivado');
      await Promise.all([refrescarEmpleado(), refrescarObligaciones ? refrescarObligaciones() : null]);
    } catch (e) {
      console.error('Error con el tour de bienvenida:', e);
      mostrarAvisoTour('error', textoErrorApi(e.response?.data?.detail, 'No se ha podido cambiar el tour de bienvenida'));
    } finally {
      setCambiandoTour(false);
    }
  };
  const tourPendiente = empleado.tour_pendiente === true && !empleado.tour_visto_en;
  const estadoTour = tourPendiente
    ? 'Guía pendiente: se abrirá en su primer inicio de sesión'
    : empleado.tour_visto_en
      ? `Tour completado el ${fechaCorta(empleado.tour_visto_en)}`
      : 'Sin activar';

  const invitacion = empleado.invitacion_portal && typeof empleado.invitacion_portal === 'object' ? empleado.invitacion_portal : null;
  const conocidos = new Set(MODULOS_ASIGNABLES.map((m) => m.id));
  const otrosPermisos = permisos.filter((p) => !conocidos.has(p));

  return (
    <div className="space-y-6">
      {/* Estado del acceso */}
      <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4" data-testid="ficha-acceso">
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Lock className="w-4 h-4 text-gray-500" /> Acceso al portal
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-gray-500">Email</dt>
            <dd className="font-medium text-gray-900 break-all">{empleado.email || 'Sin email'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Contraseña</dt>
            <dd className={`font-medium ${empleado.tiene_password ? 'text-green-700' : 'text-gray-900'}`}>
              {empleado.tiene_password ? 'Creada' : 'Sin crear'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Último acceso</dt>
            <dd className="font-medium text-gray-900">{empleado.ultimo_acceso ? fechaHoraMadrid(empleado.ultimo_acceso) : 'Nunca'}</dd>
          </div>
        </dl>
        {invitacion?.caduca_en && (
          <p className="text-sm text-gray-600" data-testid="ficha-invitacion-vigente">
            Invitación pendiente{invitacion.enviado_a ? `, enviada a ${invitacion.enviado_a}` : ' (enlace copiado a mano)'}; caduca el {fechaLarga(invitacion.caduca_en)}.
          </p>
        )}

        {puedeGestionar && (
          <div className="border-t border-gray-100 pt-4 space-y-3" data-testid="invitacion-portal">
            <p className="text-sm text-gray-600">
              Envíale un enlace para que cree él mismo su contraseña (vale 7 días y un solo uso).
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { setConfirmarInvitacion(true); setErrorInvitacion(''); setAvisoInvitacion(''); }}
                disabled={!empleado.email || enviando}
                title={!empleado.email ? 'Pon antes el email en la pestaña Datos' : undefined}
                className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="invitacion-enviar-email"
              >
                Enviar correo de bienvenida
              </button>
              <button
                type="button"
                onClick={() => generarInvitacion(false)}
                disabled={enviando}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm disabled:opacity-50"
                data-testid="invitacion-copiar-enlace"
              >
                Copiar enlace
              </button>
              <button
                type="button"
                onClick={() => setVerPasswordManual(!verPasswordManual)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline"
              >
                Poner contraseña a mano
              </button>
            </div>
            {confirmarInvitacion && (
              <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                <span className="text-gray-700">Se enviará a <strong>{empleado.email}</strong></span>
                <button type="button" onClick={() => generarInvitacion(true)} disabled={enviando} className="px-3 py-1 bg-duetto-primary text-white rounded-lg disabled:opacity-50" data-testid="invitacion-confirmar-si">
                  {enviando ? 'Enviando...' : 'Enviar'}
                </button>
                <button type="button" onClick={() => setConfirmarInvitacion(false)} disabled={enviando} className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
              </div>
            )}
            {enlace && (
              <div className="flex gap-2">
                <input type="text" readOnly value={enlace} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50" data-testid="invitacion-enlace" />
                <button type="button" onClick={copiarEnlace} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">Copiar</button>
              </div>
            )}
            {avisoInvitacion && <p className="text-sm text-green-700" data-testid="invitacion-aviso">{avisoInvitacion}</p>}
            {errorInvitacion && <p className="text-sm text-red-700" role="alert">{errorInvitacion}</p>}
            {verPasswordManual && (
              <div className="flex flex-wrap gap-2 items-end bg-gray-50 border border-gray-200 rounded-lg p-3">
                <Campo etiqueta={empleado.tiene_password ? 'Nueva contraseña' : 'Contraseña de acceso'} className="flex-1 min-w-[12rem]">
                  <input type="text" value={nuevaPassword} onChange={(e) => setNuevaPassword(e.target.value)} placeholder="Mínimo 4 caracteres" className={CLASE_INPUT} />
                </Campo>
                <button type="button" onClick={guardarPassword} disabled={!nuevaPassword || guardandoPassword} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
                  {guardandoPassword ? 'Guardando...' : 'Guardar contraseña'}
                </button>
              </div>
            )}
          </div>
        )}

        {puedeGestionar && (
          <div className="border-t border-gray-100 pt-4 space-y-3" data-testid="tour-bienvenida">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                  <Compass className="w-4 h-4 text-gray-500" /> Tour de bienvenida
                </p>
                <p className="text-sm text-gray-600 mt-0.5">
                  Una guía corta por los módulos que tiene habilitados. La ve una vez, al entrar en Mi Inicio.
                </p>
              </div>
              <span
                className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                  tourPendiente ? 'bg-amber-100 text-amber-800' : empleado.tour_visto_en ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}
                data-testid="tour-estado"
              >
                {estadoTour}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!tourPendiente && (
                <button
                  type="button"
                  onClick={() => cambiarTour(true)}
                  disabled={cambiandoTour}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm disabled:opacity-50"
                  data-testid="tour-activar"
                >
                  {cambiandoTour ? 'Guardando...' : empleado.tour_visto_en ? 'Repetir tour' : 'Activar tour de bienvenida'}
                </button>
              )}
              {tourPendiente && (
                <button
                  type="button"
                  onClick={() => cambiarTour(false)}
                  disabled={cambiandoTour}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline disabled:opacity-50"
                  data-testid="tour-quitar"
                >
                  {cambiandoTour ? 'Guardando...' : 'Quitar el tour'}
                </button>
              )}
            </div>
            <AvisoInline aviso={avisoTour} onCerrar={limpiarAvisoTour} testId="tour-aviso" />
          </div>
        )}

        {form && (
          <div className="border-t border-gray-100 pt-4 space-y-2" data-guia="pantalla-inicio" data-testid="pantalla-inicio">
            <Campo
              etiqueta="Pantalla al entrar"
              ayuda="Lo que ve al abrir el ERP. Se guarda con el botón Guardar de arriba; le llega la próxima vez que entre."
            >
              <select
                value={form.pantalla_inicio || 'auto'}
                onChange={(e) => cambiar('pantalla_inicio', e.target.value)}
                disabled={!puedeGestionar}
                className={`${CLASE_INPUT} sm:max-w-md disabled:bg-gray-50 disabled:text-gray-500`}
                data-testid="ficha-pantalla-inicio"
              >
                {OPCIONES_PANTALLA_INICIO.map((o) => (
                  <option key={o.valor} value={o.valor}>{o.texto}</option>
                ))}
              </select>
            </Campo>
          </div>
        )}
      </section>

      {/* Módulos */}
      <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4" data-testid="ficha-permisos">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Módulos a los que accede</h3>
            <p className="text-sm text-gray-500 mt-1">
              Si marcas alguno, manda sobre el rol: solo verá los módulos marcados. Sin ninguno marcado, ve lo que le da su rol.
              Dirección lo ve todo.
            </p>
          </div>
          <button
            type="button"
            onClick={onGuardarPermisos}
            disabled={!hayCambiosPermisos || guardandoPermisos}
            className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 font-medium text-sm disabled:opacity-50"
            data-testid="ficha-guardar-permisos"
          >
            {guardandoPermisos ? 'Guardando...' : 'Guardar permisos'}
          </button>
        </div>
        {MODULOS_POR_GRUPO.map(({ grupo, modulos }) => (
          <div key={grupo}>
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">{grupo}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {modulos.map((modulo) => {
                const activo = permisos.includes(modulo.id);
                return (
                  <label
                    key={modulo.id}
                    className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 cursor-pointer text-sm ${activo ? 'border-duetto-primary bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <span className="font-medium text-gray-800">{modulo.label}</span>
                    <input
                      type="checkbox"
                      checked={activo}
                      onChange={() => onTogglePermiso(modulo.id)}
                      className="w-4 h-4"
                      data-testid={`permiso-${modulo.id}`}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        {otrosPermisos.length > 0 && (
          <p className="text-xs text-gray-500">
            También tiene guardados permisos que ya no están en el menú: {otrosPermisos.join(', ')}. Se conservan.
          </p>
        )}
      </section>
    </div>
  );
};

// ==================== ALTA (/empleados/nuevo) ====================

const AltaEmpleado = ({ roles, setRoles }) => {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1);
  const [form, setForm] = useState(formAlta);
  const [inicial] = useState(form);
  // null: por defecto (marcado si hay email); true/false: lo ha tocado el usuario
  const [invitarElegido, setInvitarElegido] = useState(null);
  const [errores, setErrores] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);

  const cambiar = useCallback((campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor })), []);
  const hayCambios = JSON.stringify(form) !== JSON.stringify(inicial);
  useAvisoAlSalir(hayCambios && !guardando);

  const tieneEmail = !!String(form.email || '').trim();
  const enviarInvitacion = tieneEmail && (invitarElegido === null ? true : invitarElegido);

  const siguiente = () => {
    const lista = erroresBasicos(form);
    if (lista.length) {
      setErrores(lista.join('\n'));
      return;
    }
    setErrores('');
    setPaso(2);
  };

  const crear = async () => {
    const lista = erroresBasicos(form);
    if (lista.length) {
      setErrores(lista.join('\n'));
      setPaso(1);
      return;
    }
    setErrores('');
    setGuardando(true);
    try {
      const body = {
        ...cuerpoDesdeForm(form),
        activo: true,
        coste_despido: 0,
        enviar_invitacion: enviarInvitacion,
      };
      const res = await empleadosAPI.create(body);
      const creado = res.data?.empleado || res.data;
      if (!creado?.id) throw new Error('El servidor no ha devuelto la ficha creada');
      navigate(`/empleados/${creado.id}?tab=obligaciones`, {
        replace: true,
        state: {
          creado: true,
          avisos: res.data?.avisos || [],
          invitacion: res.data?.invitacion || null,
          invitacionError: res.data?.invitacion_error || '',
          invitacionPedida: enviarInvitacion,
        },
      });
    } catch (e) {
      console.error('Error creando el empleado:', e);
      setErrores(`No se ha podido crear el empleado:\n${textoErrorApi(e.response?.data?.detail, e.message || 'Error desconocido')}`);
      setGuardando(false);
    }
  };

  const volver = () => {
    if (hayCambios) setConfirmarSalida(true);
    else navigate('/empleados');
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={volver} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" title="Volver a Empleados" data-testid="ficha-volver">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nuevo empleado</h1>
            <p className="text-sm text-gray-500">Paso {paso} de 2: {paso === 1 ? 'persona y contrato' : 'nómina y operativa'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={`px-3 py-1 rounded-full ${paso === 1 ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-600'}`}>1. Persona y contrato</span>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className={`px-3 py-1 rounded-full ${paso === 2 ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-600'}`}>2. Nómina y operativa</span>
        </div>
      </div>

      <BloqueError texto={errores} testId="empleado-form-error" />

      {paso === 1 && (
        <div className="space-y-4 mt-4" data-testid="alta-paso-1">
          <SeccionPersona form={form} cambiar={cambiar} />
          <SeccionContrato form={form} cambiar={cambiar} roles={roles} setRoles={setRoles} />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={volver} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button type="button" onClick={siguiente} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1" data-testid="alta-siguiente">
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {paso === 2 && (
        <div className="space-y-4 mt-4" data-testid="alta-paso-2">
          <SeccionNomina form={form} cambiar={cambiar} />
          <SeccionOperativa form={form} cambiar={cambiar} />
          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <Casilla
              marcada={enviarInvitacion}
              deshabilitada={!tieneEmail}
              onCambiar={(v) => setInvitarElegido(v)}
              testId="alta-enviar-invitacion"
            >
              Enviar invitación al portal al guardar
            </Casilla>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              {tieneEmail
                ? `Le llega a ${form.email.trim()} un enlace para crear su contraseña (vale 7 días).`
                : 'Sin email no se puede invitar. Puedes hacerlo luego desde la pestaña Acceso y permisos.'}
            </p>
          </section>
          <div className="flex flex-wrap justify-between gap-3">
            <button type="button" onClick={() => setPaso(1)} disabled={guardando} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Anterior
            </button>
            <button type="button" onClick={crear} disabled={guardando} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="ficha-guardar">
              <Save className="w-4 h-4" /> {guardando ? 'Creando...' : 'Crear empleado'}
            </button>
          </div>
        </div>
      )}

      <DialogoConfirmar
        abierto={confirmarSalida}
        titulo="Salir sin guardar"
        descripcion="El empleado no se ha creado. Si sales, se pierde lo que has escrito."
        textoConfirmar="Salir sin guardar"
        textoCancelar="Seguir editando"
        peligro
        onConfirmar={() => { setConfirmarSalida(false); navigate('/empleados'); }}
        onCancelar={() => setConfirmarSalida(false)}
        testId="dialogo-salir-sin-guardar"
      />
    </div>
  );
};

// ==================== FICHA (/empleados/:id) ====================

const FichaExistente = ({ empleadoId, roles, setRoles }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Marcar obligaciones, invitar al portal y gestionar la ficha: dirección y RRHH
  const puedeGestionar = ['direccion', 'rrhh'].includes(user?.rol);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = CLAVES_PESTANAS.includes(tabParam) ? tabParam : 'datos';

  const [empleado, setEmpleado] = useState(null);
  const [errorCarga, setErrorCarga] = useState('');
  const [form, setForm] = useState(null);
  const [formOriginal, setFormOriginal] = useState(null);
  const [permisos, setPermisos] = useState([]);
  const [permisosOriginal, setPermisosOriginal] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState('');
  const [avisosServidor, setAvisosServidor] = useState([]);
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal();
  // Mensajes del alta (vienen en el estado de la navegación) que no caducan solos
  const [mensajeAlta, setMensajeAlta] = useState(null);

  const [obligaciones, setObligaciones] = useState(null);
  const [errorObligaciones, setErrorObligaciones] = useState('');

  // Baja y reactivación
  const [bajaFecha, setBajaFecha] = useState(null);
  const [reactivarAbierto, setReactivarAbierto] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');
  const [confirmarSalida, setConfirmarSalida] = useState(false);

  // Carga la ficha; con `reiniciarForm` el formulario vuelve a lo guardado
  const cargarEmpleado = useCallback(async ({ reiniciarForm = false } = {}) => {
    try {
      const res = await empleadosAPI.getOne(empleadoId);
      const emp = res.data;
      setEmpleado(emp);
      setErrorCarga('');
      if (reiniciarForm) {
        const f = formDesdeEmpleado(emp);
        setForm(f);
        setFormOriginal(f);
        const p = Array.isArray(emp.permisos_modulos) ? emp.permisos_modulos : [];
        setPermisos(p);
        setPermisosOriginal(p);
      }
      return emp;
    } catch (e) {
      console.error('Error cargando la ficha:', e);
      setErrorCarga(textoErrorApi(e.response?.data?.detail, 'No se ha podido cargar la ficha de este empleado'));
      return null;
    }
  }, [empleadoId]);

  const refrescarEmpleado = useCallback(() => cargarEmpleado(), [cargarEmpleado]);

  const cargarObligaciones = useCallback(async () => {
    setErrorObligaciones('');
    try {
      const res = await obligacionesAPI.empleado(empleadoId);
      setObligaciones(res.data || null);
    } catch (e) {
      console.error('Error cargando obligaciones:', e);
      setObligaciones(null);
      setErrorObligaciones(textoErrorApi(e.response?.data?.detail, 'No se han podido cargar las obligaciones'));
    }
  }, [empleadoId]);

  useEffect(() => {
    cargarEmpleado({ reiniciarForm: true });
    cargarObligaciones();
  }, [cargarEmpleado, cargarObligaciones]);

  // Lo que deja el alta: avisos del servidor y resultado de la invitación
  useEffect(() => {
    const estado = location.state;
    if (!estado?.creado) return;
    setAvisosServidor(Array.isArray(estado.avisos) ? estado.avisos : []);
    let texto = 'Empleado creado.';
    let tipo = 'ok';
    if (estado.invitacion?.enviado_a) {
      texto += ` Invitación al portal enviada a ${estado.invitacion.enviado_a}${estado.invitacion.caduca_en ? `, caduca el ${fechaLarga(estado.invitacion.caduca_en)}` : ''}.`;
    } else if (estado.invitacionError) {
      texto += ` La invitación al portal no se ha podido enviar: ${estado.invitacionError}. Puedes reenviarla desde Acceso y permisos.`;
      tipo = 'error';
    } else if (estado.invitacionPedida) {
      texto += ' La invitación al portal no consta como enviada: compruébalo en Acceso y permisos.';
      tipo = 'aviso';
    }
    setMensajeAlta({ tipo, texto });
    // Se quita del historial para que no se repita al recargar
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const cambiar = useCallback((campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor })), []);

  const hayCambiosForm = !!form && !!formOriginal && JSON.stringify(form) !== JSON.stringify(formOriginal);
  const hayCambiosPermisos = JSON.stringify([...permisos].sort()) !== JSON.stringify([...permisosOriginal].sort());
  const hayCambios = hayCambiosForm || hayCambiosPermisos;
  useAvisoAlSalir(hayCambios);

  const cambiarTab = (valor) => {
    const siguientes = new URLSearchParams(searchParams);
    siguientes.set('tab', valor);
    setSearchParams(siguientes, { replace: true });
  };

  const guardarPermisos = async () => {
    setGuardandoPermisos(true);
    try {
      await empleadosAPI.updatePermisos(empleadoId, permisos);
      const fresco = await cargarEmpleado();
      const guardados = Array.isArray(fresco?.permisos_modulos) ? fresco.permisos_modulos : permisos;
      setPermisos(guardados);
      setPermisosOriginal(guardados);
      // El Sidebar actualiza la vista simulada del empleado si está activa
      window.dispatchEvent(new CustomEvent('empleadoPermisosUpdated', { detail: { empleadoId, permisos: guardados } }));
      return true;
    } catch (e) {
      console.error('Error guardando permisos:', e);
      setErrorGuardar(textoErrorApi(e.response?.data?.detail, 'Error al guardar los permisos'));
      return false;
    } finally {
      setGuardandoPermisos(false);
    }
  };

  const guardar = async () => {
    setErrorGuardar('');
    limpiarAviso();
    if (hayCambiosForm) {
      const lista = erroresBasicos(form);
      if (lista.length) {
        setErrorGuardar(lista.join('\n'));
        return;
      }
    }
    setGuardando(true);
    try {
      let avisos = [];
      if (hayCambiosForm) {
        const cambios = cuerpoCambios(form, formOriginal);
        if (Object.keys(cambios).length) {
          const res = await empleadosAPI.update(empleadoId, cambios);
          avisos = Array.isArray(res.data?.avisos) ? res.data.avisos : [];
        }
        const fresco = await cargarEmpleado();
        if (fresco) {
          const f = formDesdeEmpleado(fresco);
          setForm(f);
          setFormOriginal(f);
        }
      }
      let permisosOk = true;
      if (hayCambiosPermisos) permisosOk = await guardarPermisos();
      setAvisosServidor(avisos);
      if (permisosOk) mostrarAviso('ok', 'Cambios guardados');
    } catch (e) {
      console.error('Error guardando la ficha:', e);
      setErrorGuardar(`No se han podido guardar los cambios:\n${textoErrorApi(e.response?.data?.detail, e.message || 'Error desconocido')}`);
    } finally {
      setGuardando(false);
    }
  };

  const guardarSoloPermisos = async () => {
    setErrorGuardar('');
    const ok = await guardarPermisos();
    if (ok) mostrarAviso('ok', 'Permisos actualizados');
  };

  const togglePermiso = (id) => {
    setPermisos((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const confirmarBaja = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(bajaFecha || '')) {
      setErrorDialogo('Indica la fecha de baja');
      return;
    }
    setProcesando(true);
    setErrorDialogo('');
    try {
      const res = await api.post(`/empleados/${empleadoId}/dar-de-baja`, null, { params: { fecha_baja: bajaFecha } });
      setBajaFecha(null);
      await cargarEmpleado();
      mostrarAviso('ok', res.data?.message || 'Baja registrada');
    } catch (e) {
      console.error('Error dando de baja:', e);
      setErrorDialogo(textoErrorApi(e.response?.data?.detail, 'Error al dar de baja'));
    } finally {
      setProcesando(false);
    }
  };

  const confirmarReactivar = async () => {
    setProcesando(true);
    setErrorDialogo('');
    try {
      await empleadosAPI.update(empleadoId, { activo: true, baja: null });
      setReactivarAbierto(false);
      await cargarEmpleado();
      mostrarAviso('ok', 'Vuelve a estar activo');
    } catch (e) {
      console.error('Error reactivando:', e);
      setErrorDialogo(textoErrorApi(e.response?.data?.detail, 'Error al reactivar'));
    } finally {
      setProcesando(false);
    }
  };

  const volver = () => {
    if (hayCambios) setConfirmarSalida(true);
    else navigate('/empleados');
  };

  const nombreRol = useMemo(
    () => roles.find((r) => r.role_id === empleado?.rol)?.display_name || empleado?.rol || '',
    [roles, empleado],
  );

  if (errorCarga && !empleado) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl space-y-4">
        <BloqueError texto={errorCarga} testId="ficha-error-carga" />
        <button type="button" onClick={() => navigate('/empleados')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Volver a Empleados
        </button>
      </div>
    );
  }

  if (!empleado || !form) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Cargando ficha...</div>
      </div>
    );
  }

  const activo = empleado.activo !== false;
  const bajaProgramada = activo && !!empleado.baja;
  const estado = !activo
    ? { texto: `Baja${empleado.baja ? ` ${fechaLarga(empleado.baja)}` : ''}`, clase: 'bg-red-100 text-red-800' }
    : bajaProgramada
      ? { texto: `Baja programada ${fechaLarga(empleado.baja)}`, clase: 'bg-amber-100 text-amber-800' }
      : { texto: 'Activo', clase: 'bg-green-100 text-green-800' };
  const faltan = obligaciones?.faltan || 0;

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      {/* Cabecera */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={volver} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" title="Volver a Empleados" data-testid="ficha-volver">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${clasesColor(empleado.color)}`}>
            {empleado.nombre?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate" data-testid="ficha-nombre-cabecera">{empleado.nombre}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
              {empleado.nombre_completo && empleado.nombre_completo !== empleado.nombre && (
                <span className="text-sm text-gray-500">{empleado.nombre_completo}</span>
              )}
              {nombreRol && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">{nombreRol}</span>}
              <span className={`px-2 py-0.5 rounded-full font-medium ${estado.clase}`} data-testid="ficha-estado">{estado.texto}</span>
              {faltan > 0 && (
                <button
                  type="button"
                  onClick={() => cambiarTab('obligaciones')}
                  className="px-2 py-0.5 bg-red-100 text-red-700 font-bold rounded-full hover:bg-red-200"
                  data-testid="ficha-faltan"
                >
                  faltan {faltan}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hayCambios && (
            <button
              type="button"
              onClick={guardar}
              disabled={guardando || guardandoPermisos}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="ficha-guardar"
            >
              <Save className="w-4 h-4" /> {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          )}
          {puedeGestionar && activo && !bajaProgramada && (
            <button
              type="button"
              onClick={() => { setErrorDialogo(''); setBajaFecha(hoyMadrid()); }}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 flex items-center gap-2"
              data-testid="ficha-baja"
            >
              <Trash2 className="w-4 h-4" /> Dar de baja
            </button>
          )}
          {puedeGestionar && (!activo || bajaProgramada) && (
            <button
              type="button"
              onClick={() => { setErrorDialogo(''); setReactivarAbierto(true); }}
              className="px-4 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 flex items-center gap-2"
              data-testid="ficha-reactivar"
            >
              <RotateCcw className="w-4 h-4" /> {activo ? 'Anular la baja' : 'Reactivar'}
            </button>
          )}
          {user?.rol === 'direccion' && (
            <Link
              to={`/uso-erp?usuario_id=${encodeURIComponent(empleado.id || empleadoId)}`}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              data-uso="empleado.ver_uso_erp"
              data-testid="ficha-ver-uso-erp"
            >
              Ver uso del ERP
            </Link>
          )}
          <button type="button" onClick={volver} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            Volver
          </button>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        {mensajeAlta && (
          <div
            className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm ${
              mensajeAlta.tipo === 'error' ? 'bg-red-50 border-red-200 text-red-700'
                : mensajeAlta.tipo === 'aviso' ? 'bg-amber-50 border-amber-200 text-amber-800'
                  : 'bg-green-50 border-green-200 text-green-800'
            }`}
            role="status"
            data-testid="ficha-mensaje-alta"
          >
            <span>{mensajeAlta.texto}</span>
            <button type="button" onClick={() => setMensajeAlta(null)} className="shrink-0 opacity-60 hover:opacity-100" title="Cerrar aviso">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <AvisoInline aviso={aviso} onCerrar={limpiarAviso} testId="ficha-aviso" />
        <AvisosServidor avisos={avisosServidor} onCerrar={() => setAvisosServidor([])} />
        <BloqueError texto={errorGuardar} testId="empleado-form-error" />
        {hayCambios && (
          <p className="text-sm text-amber-700" data-testid="ficha-cambios-sin-guardar">Hay cambios sin guardar.</p>
        )}
      </div>

      <Tabs value={tab} onValueChange={cambiarTab}>
        <TabsList className="h-auto flex flex-wrap justify-start gap-1 bg-gray-100 p-1 mb-4">
          {PESTANAS.map(({ clave, texto, icono: Icono }) => (
            <TabsTrigger key={clave} value={clave} className="flex items-center gap-1.5 px-3 py-1.5" data-testid={`ficha-tab-${clave}`}>
              <Icono className="w-4 h-4" />
              {texto}
              {clave === 'obligaciones' && faltan > 0 && (
                <span className="ml-1 bg-red-100 text-red-700 text-[11px] font-bold rounded-full px-1.5">{faltan}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="datos" className="space-y-4">
          <SeccionPersona form={form} cambiar={cambiar} />
          <SeccionOperativa form={form} cambiar={cambiar} />
        </TabsContent>

        <TabsContent value="contrato" className="space-y-4">
          <SeccionContrato form={form} cambiar={cambiar} roles={roles} setRoles={setRoles} />
          <SeccionNomina form={form} cambiar={cambiar} />
        </TabsContent>

        <TabsContent value="obligaciones">
          <PestanaObligaciones
            empleado={empleado}
            obligaciones={obligaciones}
            error={errorObligaciones}
            puedeGestionar={puedeGestionar}
            mostrarAviso={mostrarAviso}
            onCambio={async () => { await Promise.all([cargarObligaciones(), cargarEmpleado()]); }}
            onIrAPestana={cambiarTab}
          />
        </TabsContent>

        <TabsContent value="documentos">
          <PestanaDocumentos empleadoId={empleadoId} mostrarAviso={mostrarAviso} />
        </TabsContent>

        <TabsContent value="fichajes">
          <RegistroHorarioEmpleado empleadoId={empleadoId} />
        </TabsContent>

        <TabsContent value="acceso">
          <PestanaAcceso
            empleado={empleado}
            puedeGestionar={puedeGestionar}
            permisos={permisos}
            onTogglePermiso={togglePermiso}
            hayCambiosPermisos={hayCambiosPermisos}
            onGuardarPermisos={guardarSoloPermisos}
            guardandoPermisos={guardandoPermisos}
            refrescarEmpleado={refrescarEmpleado}
            refrescarObligaciones={cargarObligaciones}
            mostrarAviso={mostrarAviso}
            form={form}
            cambiar={cambiar}
          />
        </TabsContent>
      </Tabs>

      {/* Dar de baja */}
      <DialogoConfirmar
        abierto={bajaFecha !== null}
        titulo={`Dar de baja a ${empleado.nombre}`}
        descripcion="Si la fecha es futura, queda como baja programada hasta ese día."
        textoConfirmar="Dar de baja"
        peligro
        ocupado={procesando}
        deshabilitado={!bajaFecha}
        error={errorDialogo}
        onConfirmar={confirmarBaja}
        onCancelar={() => { setBajaFecha(null); setErrorDialogo(''); }}
        testId="dialogo-baja"
      >
        <label className="block text-sm font-medium text-gray-700">
          Fecha de baja
          <input
            type="date"
            value={bajaFecha || ''}
            onChange={(e) => setBajaFecha(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            data-testid="dialogo-baja-fecha"
          />
        </label>
      </DialogoConfirmar>

      {/* Reactivar o anular una baja programada */}
      <DialogoConfirmar
        abierto={reactivarAbierto}
        titulo={activo ? `Anular la baja de ${empleado.nombre}` : `Reactivar a ${empleado.nombre}`}
        descripcion={activo ? 'Se borra la fecha de baja programada y sigue activo.' : 'Vuelve a estar activo y se borra la fecha de baja.'}
        textoConfirmar={activo ? 'Anular la baja' : 'Reactivar'}
        ocupado={procesando}
        error={errorDialogo}
        onConfirmar={confirmarReactivar}
        onCancelar={() => { setReactivarAbierto(false); setErrorDialogo(''); }}
        testId="dialogo-reactivar"
      />

      {/* Salir con cambios sin guardar */}
      <DialogoConfirmar
        abierto={confirmarSalida}
        titulo="Salir sin guardar"
        descripcion="Hay cambios sin guardar en la ficha. Si sales, se pierden."
        textoConfirmar="Salir sin guardar"
        textoCancelar="Seguir editando"
        peligro
        onConfirmar={() => { setConfirmarSalida(false); navigate('/empleados'); }}
        onCancelar={() => setConfirmarSalida(false)}
        testId="dialogo-salir-sin-guardar"
      />
    </div>
  );
};

// ==================== PÁGINA ====================

const FichaEmpleado = () => {
  const { id } = useParams();
  const [roles, setRoles] = useState([]);

  // Roles: una sola vez al montar
  useEffect(() => {
    let vivo = true;
    api.get('/roles')
      .then((res) => { if (vivo) setRoles(res.data || []); })
      .catch((e) => console.error('Error cargando roles:', e));
    return () => { vivo = false; };
  }, []);

  if (!id) return <AltaEmpleado roles={roles} setRoles={setRoles} />;
  // key: al pasar de una ficha a otra, estado limpio
  return <FichaExistente key={id} empleadoId={id} roles={roles} setRoles={setRoles} />;
};

export default FichaEmpleado;
